<!-- src/shared/components/layout/TransactionalFooter.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import FeedbackToggle from '@/shared/components/ui/FeedbackToggle.vue';
  import JurisdictionToggle from '@/shared/components/ui/JurisdictionToggle.vue';
  import LanguageToggle from '@/shared/components/ui/LanguageToggle.vue';
  import FooterLinks from '@/shared/components/layout/FooterLinks.vue';
  import ThemeToggle from '@/shared/components/ui/ThemeToggle.vue';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { useProductIdentity } from '@/shared/stores/identityStore';
  import { useFooterConfig } from '@/shared/composables/useFooterConfig';
  import type { LayoutProps } from '@/types/ui/layouts';
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';

  withDefaults(defineProps<LayoutProps>(), {
    displayFeedback: true,
    displayFooterLinks: true,
    displayVersion: true,
    displayToggles: true,
    displayPoweredBy: true,
  });

  const { t } = useI18n();
  const bootstrapStore = useBootstrapStore();
  const {
    regions_enabled,
    regions,
    authentication,
    i18n_enabled,
    ot_version_long,
    ui,
  } = storeToRefs(bootstrapStore);

  const { isCustom } = storeToRefs(useProductIdentity());
  const { showVersionConfig } = useFooterConfig();

  // Hide regions toggle on custom domains (they're tied to a specific deployment)
  const showRegionsToggle = computed(
    () => regions_enabled.value && !!regions?.value && !isCustom.value
  );
</script>

<template>
  <!-- prettier-ignore-attribute class -->
  <footer
    class="
    w-full min-w-[320px]
    bg-gray-100
    py-16 transition-all
    duration-300 dark:bg-gray-800"
    :aria-label="t('web.layout.site_footer')">
    <div class="container mx-auto max-w-2xl px-4">
      <!-- Footer Links Section -->
      <FooterLinks v-if="displayFooterLinks" />

      <!-- Existing Footer Content -->
      <!-- prettier-ignore-attribute class -->
      <div
        class="
        flex
        flex-col-reverse items-center
        justify-between
        space-y-6 space-y-reverse md:flex-row
        md:space-y-0"
        :class="
          displayFooterLinks && ui?.footer_links?.enabled
            ? 'mt-8 border-t border-gray-200 pt-8 dark:border-gray-700'
            : ''
        ">
        <!-- Version and Powered By -->
        <!-- prettier-ignore-attribute class -->
        <div
          class="
          flex w-full
          flex-wrap items-center justify-center
          text-center
          text-xs text-gray-500 dark:text-gray-400 md:w-auto md:justify-start md:text-left">
          <div class="flex flex-col items-center gap-y-1 md:items-start">
            <!-- Zeile 1: Verweise -->
            <div class="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 md:justify-start">
              <a
                href="https://notory.io/"
                target="_blank"
                rel="noopener noreferrer"
                class="transition-colors hover:text-brand-500 dark:hover:text-brand-400">
                ein Tool von Notory
              </a>
              <span aria-hidden="true">&middot;</span>
              <a
                href="https://notory.io/impressum.html"
                target="_blank"
                rel="noopener noreferrer"
                class="transition-colors hover:text-brand-500 dark:hover:text-brand-400">
                Impressum
              </a>
              <span aria-hidden="true">&middot;</span>
              <a
                href="https://notory.io/datenschutz.html"
                target="_blank"
                rel="noopener noreferrer"
                class="transition-colors hover:text-brand-500 dark:hover:text-brand-400">
                Datenschutz
              </a>
              <span aria-hidden="true">&middot;</span>
              <a
                href="/api-docs"
                class="transition-colors hover:text-brand-500 dark:hover:text-brand-400">
                API
              </a>
            </div>
            <!-- Zeile 2: Version + Copyright -->
            <div class="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 md:justify-start">
              <span v-if="displayVersion && showVersionConfig">Notory Vault v{{ ot_version_long }}</span>
              <span
                v-if="displayVersion && showVersionConfig"
                aria-hidden="true">&middot;</span>
              <span>&copy; 2026 TougeTech - Raphael Ernst</span>
            </div>
          </div>
        </div>

        <!-- Toggles Section -->
        <!-- prettier-ignore-attribute class -->
        <div
          v-if="displayToggles"
          class="flex w-full flex-row items-center justify-center gap-4 sm:w-auto sm:justify-end">
          <JurisdictionToggle v-if="showRegionsToggle" />

          <!-- prettier-ignore-attribute class -->
          <!-- Dark/Light-Umschalter ausgeblendet; Sprachumschalter ist oben rechts (MastHead) -->
          <ThemeToggle
            v-if="false"
            class="text-gray-500 transition-colors duration-200
              hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100"
            :aria-label="t('web.layout.toggle_dark_mode')" />

          <LanguageToggle
            v-if="false"
            :compact="true"
            max-height="max-h-dvh" />

          <!-- prettier-ignore-attribute class -->
          <FeedbackToggle
            v-if="displayFeedback && authentication?.enabled"
            class="text-gray-500 transition-colors duration-200
              hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100"
            :aria-label="t('web.layout.provide_feedback')" />
        </div>
      </div>
    </div>
  </footer>
</template>
